import { formatDisplayDate } from "../utils/displayDate";
import type { Template, TemplateStatus, TemplateFormData } from "../types/templates";
import { BASE_URL } from "./contractsService";
import { mockTemplates } from "../data/templates";
import { apiRequest } from "./apiClient";

export type TemplateSortBy =
  | "created_at"
  | "template_name"
  | "created_by";

export type TemplateListParams = {
  readonly sort_by?: TemplateSortBy;
  readonly sort_dir?: "asc" | "desc";
};

type BackendTemplateListItem = {
  readonly id: string;
  readonly template_name?: string | null;
  readonly template_category?: string | null;
  readonly template_type?: string | null;
  readonly template_download_url?: string | null;
  readonly business_units?: string[] | null;
  readonly status?: string | null;
  readonly created_at?: string | null;
  readonly updated_at?: string | null;
  readonly created_by?: string | null;
  readonly created_by_name?: string | null;
  readonly created_by_email?: string | null;
};

type BackendTemplatesListResponse = {
  readonly data: BackendTemplateListItem[];
  readonly meta?: unknown;
};

type BackendStatusUpdateResponse = {
  readonly data: {
    readonly id: string;
    readonly status: TemplateStatus;
  };
};

export const listTemplates = async (
  params?: TemplateListParams
): Promise<Template[]> => {
  try {
    const url = new URL(`${BASE_URL}/templates`, globalThis.location.origin);
    if (params?.sort_by) {
      url.searchParams.set("sort_by", params.sort_by);
    }
    if (params?.sort_dir) {
      url.searchParams.set("sort_dir", params.sort_dir);
    }
    const response = await apiRequest(url.toString());

    const json = (await response.json()) as BackendTemplatesListResponse;
    if (!Array.isArray(json.data)) {
      return [];
    }

    return json.data.map((item) => {
      const name =
        item.template_name && item.template_name.trim().length > 0
          ? item.template_name
          : "Untitled Template";

      const createdAt = item.created_at ?? item.updated_at ?? new Date().toISOString();
      const createdDate = formatDisplayDate(createdAt);

      return {
        id: item.id,
        name,
        createdBy: item.created_by_name ?? "Unknown",
        createdDate,
        status: (item.status as TemplateStatus | null) ?? "Active",
        templateCategory: item.template_category ?? "",
        contractCategory: item.template_type ?? "",
        businessUnits: item.business_units ?? [],
        fileName: `${name}.docx`,
        templateDownloadUrl: item.template_download_url ?? null,
      };
    });
  } catch {
    return [...mockTemplates];
  }
};

export const uploadTemplate = async (formData: TemplateFormData): Promise<void> => {
  const body = new FormData();
  if (formData.file) {
    body.append("file", formData.file);
  }
  body.append("template_category", formData.templateCategory);
  body.append("template_type", formData.contractCategory);
  body.append("business_units", JSON.stringify(formData.businessUnits));

  await apiRequest("/templates", {
    method: "POST",
    body,
  });
};

export const updateTemplateStatus = async (
  id: string,
  status: TemplateStatus,
): Promise<TemplateStatus> => {
  const response = await apiRequest(`/templates/${id}/status`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status }),
  });

  const json = (await response.json()) as BackendStatusUpdateResponse;
  return json.data.status;
};
